/**
 * Labstep
 *
 * @module components/ProtocolStep/Action/Complete
 * @desc Component for marking protocol step as complete
 */

import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { ProtocolStepActionStartEnd } from 'labstep-web/components/ProtocolStep/Action/StartEnd';
import { ICONS } from 'labstep-web/constants/icons';
import { Icon } from 'labstep-web/core/Icon';
import { PopupContainer as Popup } from 'labstep-web/core/Popup';
import { Experiment } from 'labstep-web/models/experiment.model';
import { humanReadableDate } from 'labstep-web/services/date.service';
import type React from 'react';
import type { ProtocolStepActionCompleteProps } from './types';

export const ProtocolStepActionComplete: React.FC<
  ProtocolStepActionCompleteProps
> = ({ protocol, protocolStep }) => {
  const canEdit = useHasAccess(
    protocolStep.entityName,
    protocolStep.guid,
    Action.edit,
    'ended_at',
  );
  const extraComponentProps = canEdit ? {} : { onClick: undefined };
  let content = 'Incomplete';
  if (protocolStep.ended_at) {
    content = `Completed on ${humanReadableDate(
      protocolStep.ended_at,
    )}`;
  } else if (canEdit) {
    content = 'Mark as complete';
  }

  if (!(protocol instanceof Experiment)) {
    return null;
  }
  return protocol.isTemplate ? (
    <Popup
      trigger={
        <Icon size="big" color="grey" name={ICONS.protocol.step} />
      }
      content="You can mark steps as complete each time you run the protocol."
      inverted
    />
  ) : (
    <ProtocolStepActionStartEnd
      endedAt={protocolStep.ended_at ? null : undefined}
      protocolStep={protocolStep}
      actionComponentProps={{
        type: 'icon',
        disabled: !canEdit,
        icon: ICONS.protocol.step,
        elementProps: {
          size: 'big',
          popup: {
            content,
          },
          color: protocolStep.ended_at ? 'green' : 'yellow',
          noBackground: true,
          ...extraComponentProps,
        },
      }}
    />
  );
};
